import { v4 } from "uuid";
import { uniq, inRange } from "lodash";

import { IProcurementMethod } from "models/ProcurementMethod/procurementMethodModel";
import {
	ItemDetailGroupByAvailability,
	ProcurementMethodSelectionItem,
	UpdateRequisitionLineProcurementMethod,
} from "models/Requisition/RequisitionProcurementMethodSelection";
import { ItemSourcingType } from "./enums/requisition-entry";

export type ReqProcMethodSelectionType = {
	showProcurementMethodSection: boolean;
	updateProcMethodList: UpdateRequisitionLineProcurementMethod[];
	selectedProcMethodList: {
		itemName: string;
		itemAvailability: string;
		procMethod: string;
	}[];
};

export const widelyAvailability = "Widely";
export const widelyAvailabilityId = 1;
export const transversalTenderProcMethod = "TRNVSL TND";
export const closedTenderProcMethod = "CLS TND";
export const openTenderProcMethod = "OPN TND";
export const singleSourcingTenderProcMethod = "SGL SOU TND";
export const rfqLimitedProcMethod = "RFQ - LTD";
export const rfqWidelyProcMethod = "RFQ - WLD";
export const transversalRfqProcMethod = "TRNVSL RFQ";
export const singleSourcingRfqProcMethod = "SGL SOU RFQ";

class ProcurementMethodUtilInternal {
	// get total amount by item list
	getTotalByItemList = (itemList: ProcurementMethodSelectionItem[]) => {
		return itemList.reduce(
			(total, currentValue) => (total = total + currentValue.totalEstCost),
			0
		);
	};

	// get available procurement method from totalAmount and availabilityId
	getProcurementMethodFromTotal = (
		totalAmount: number,
		availabilityId: number,
		procurementMethodList: IProcurementMethod[]
	) => {
		return procurementMethodList.find(
			(e) =>
				e.itemAvailabilityId === availabilityId &&
				totalAmount >= e.thresholdFrom &&
				totalAmount <= e.thresholdTo
		);
	};

	getItemSourcingTypeId = (itemList: ProcurementMethodSelectionItem[]) => {
		return uniq(itemList.map((r) => r.itemAvailability)).length === 1 ||
			uniq(
				itemList
					.filter(
						(i) => i.procurementMethod !== null && i.procurementMethod !== ""
					)
					.map((r) => r.itemAvailability)
			).length === 1
			? ItemSourcingType.Combined
			: ItemSourcingType.Split;
	};

	getItemListBySourcingType = (
		itemList: ProcurementMethodSelectionItem[],
		procurementMethodList: IProcurementMethod[],
		isEditMode = true
	) => {
		const itemAvailabilityList = uniq(itemList.map((r) => r.itemAvailability));
		const tempItemListBySplitType: ItemDetailGroupByAvailability[] = [];
		const tempItemList: ProcurementMethodSelectionItem[] = itemList;
		tempItemList.forEach((pmItem: ProcurementMethodSelectionItem) => {
			pmItem.id = v4();
		});
		// set item list for group by split type
		itemAvailabilityList.forEach((item) => {
			const subItemList = tempItemList.filter(
				(r) => r.itemAvailability === item
			);
			const tempItemDetail = new ItemDetailGroupByAvailability();
			const procMethodObjForSplit = isEditMode
				? ProcurementMethodUtil.getProcurementMethodFromTotal(
						ProcurementMethodUtil.getTotalByItemList(subItemList),
						subItemList[0].itemAvailabilityId,
						procurementMethodList
				  )
				: undefined;
			tempItemListBySplitType.push({
				...tempItemDetail,
				itemAvailability: item,
				itemAvailabilityId: subItemList[0].itemAvailabilityId,
				items: subItemList,
				procurementMethod:
					!isEditMode && subItemList[0].procurementMethod
						? subItemList[0].procurementMethod
						: procMethodObjForSplit
						? procMethodObjForSplit.procurementMethodCategory
						: "",
				procurementMethodId:
					!isEditMode && subItemList[0].procurementMethodId
						? subItemList[0].procurementMethodId
						: procMethodObjForSplit
						? procMethodObjForSplit.procurementMethodId
						: null,
				defaultProcurementMethodId:
					!isEditMode && subItemList[0].procurementMethodId
						? subItemList[0].procurementMethodId
						: procMethodObjForSplit
						? procMethodObjForSplit.procurementMethodId
						: null,
			});
		});
		// set item list for group by combined type
		const tempItemListByCombineType: ItemDetailGroupByAvailability[] = [];
		const tempDetail = new ItemDetailGroupByAvailability();
		const tempSubItemList: ProcurementMethodSelectionItem[] = [];
		itemList.map((item) => tempSubItemList.push(item));
		const procMethodObjForCombine = isEditMode
			? ProcurementMethodUtil.getProcurementMethodFromTotal(
					ProcurementMethodUtil.getTotalByItemList(tempSubItemList),
					tempSubItemList[0].itemAvailabilityId,
					procurementMethodList
			  )
			: undefined;
		tempItemListByCombineType.push({
			...tempDetail,
			itemAvailability: itemList[0].itemAvailability,
			itemAvailabilityId: itemList[0].itemAvailabilityId,
			items: tempSubItemList,
			procurementMethod:
				!isEditMode && itemList[0].procurementMethod
					? itemList[0].procurementMethod
					: procMethodObjForCombine
					? procMethodObjForCombine.procurementMethodCategory
					: "",
			procurementMethodId:
				!isEditMode && itemList[0].procurementMethodId
					? itemList[0].procurementMethodId
					: procMethodObjForCombine
					? procMethodObjForCombine.procurementMethodId
					: null,
			defaultProcurementMethodId:
				!isEditMode && itemList[0].procurementMethodId
					? itemList[0].procurementMethodId
					: procMethodObjForCombine
					? procMethodObjForCombine.procurementMethodId
					: null,
		});
		return [tempItemListBySplitType, tempItemListByCombineType];
	};

	setUpdateProcurementMethodList = (
		object: ProcurementMethodSelectionItem,
		procurementMethodId: number,
		defaultProcurementMethodId: number | null,
		comment: string
	) => {
		return {
			requisitionLineId: object.requisitionLineId,
			itemAvailabilityId: object.itemAvailabilityId,
			procurementMethodId: procurementMethodId || 0,
			defaultProcurementMethodId: defaultProcurementMethodId,
			comment: comment,
		};
	};

	getUpdateProcurementMethodList = (
		itemList: ItemDetailGroupByAvailability[],
		itemSourcingTypeId: number
	) => {
		const tempList: UpdateRequisitionLineProcurementMethod[] = [];
		if (itemSourcingTypeId === ItemSourcingType.Combined) {
			itemList
				.flatMap((m) => m.items)
				.forEach((item: ProcurementMethodSelectionItem) => {
					if (itemList[0].procurementMethodId)
						tempList.push(
							this.setUpdateProcurementMethodList(
								item,
								itemList[0].procurementMethodId,
								itemList[0].defaultProcurementMethodId,
								itemList[0].comment
							)
						);
				});
		} else {
			itemList.forEach((i) => {
				i.items.forEach((item) => {
					if (i.procurementMethodId)
						tempList.push(
							this.setUpdateProcurementMethodList(
								item,
								i.procurementMethodId,
								i.defaultProcurementMethodId,
								i.comment
							)
						);
				});
			});
		}
		return tempList;
	};

	getSelectedProcurementMethodList = (
		itemList: ProcurementMethodSelectionItem[],
		allProcMethodList: IProcurementMethod[],
		updateProcMethodList: UpdateRequisitionLineProcurementMethod[]
	) => {
		const selectedProcMethodList: {
			itemName: string;
			itemAvailability: string;
			procMethod: string;
		}[] = [];
		if (itemList.length && updateProcMethodList.length) {
			updateProcMethodList.forEach((item) => {
				const itemDetail = itemList.find(
					(i) => i.requisitionLineId === item.requisitionLineId
				);
				if (itemDetail) {
					selectedProcMethodList.push({
						itemName: itemDetail.item,
						itemAvailability:
							itemList.find(
								(i) => i.itemAvailabilityId === item.itemAvailabilityId
							)?.itemAvailability || "",
						procMethod:
							allProcMethodList.find(
								(pm) => pm.procurementMethodId === item.procurementMethodId
							)?.procurementMethodCategory || "",
					});
				}
			});
		}
		return selectedProcMethodList;
	};

	getTransversalProcMethod = (
		procurementMethodList: IProcurementMethod[],
		amount: number
	) => {
		return procurementMethodList.find(
			(p) =>
				p.isTransversalContract &&
				inRange(amount, p.thresholdFrom, p.thresholdTo + 0.01)
		);
	};
}

export const ProcurementMethodUtil = new ProcurementMethodUtilInternal();
